import React, { useState, useEffect, useCallback, useRef } from "react";
import { FileSpreadsheet, Table2 } from "lucide-react";
import api from "../../utils/api";
import ExcelUploadManager from "./ExcelUploadManager";
import ExcelShipmentHistory from "./ExcelShipmentHistory";
import { resolveView, buildDashboard, bottomKpis } from "./controlTower/dashboardModel";
import useExcelRows from "./controlTower/useExcelRows";
import DashboardHeader from "./controlTower/DashboardHeader";
import { TopKpiCards, BottomKpiCards } from "./controlTower/KpiCards";
import ShipmentMap from "./controlTower/ShipmentMap";
import StatusDonut from "./controlTower/StatusDonut";
import FreightTrendCard from "./controlTower/FreightTrendCard";
import ExceptionCenter from "./controlTower/ExceptionCenter";
import TasksAlerts from "./controlTower/TasksAlerts";
import ShippingTermsOverview from "./controlTower/ShippingTermsOverview";
import ShipmentsTable from "./controlTower/ShipmentsTable";
import IncotermsModal from "./controlTower/IncotermsModal";
import PalletsView from "./controlTower/pallets/PalletsView";
import OutboundInsights, { Overlay } from "./controlTower/OutboundInsights";
import { OUTBOUND_DC } from "./controlTower/constants";

// Control Tower: platform data (/api/control-tower/summary) + the latest accepted
// Excel reports (/api/excel-ingestion). The header view switch decides which one each
// section shows — see resolveView / buildDashboard in controlTower/dashboardModel.js.
// tenantOnly (Hasbro workspace): only the tenant's own Excel/pallet data — the platform
// summary spans every tenant, so it is neither fetched nor offered as a view.
export default function ControlTower({ tenantOnly = false }) {
    const [data, setData]                   = useState(null);
    const [loading, setLoading]             = useState(true);
    const [error, setError]                 = useState(null);
    const [lastRefresh, setLast]            = useState(null);
    const [excel, setExcel]                 = useState(null);   // latest accepted Excel reports
    // "inbound" | "outbound" | "all" | "platform" | "pallets" — ?view=pallets opens the Pallets view (email links)
    const [view, setView]                   = useState(() => (new URLSearchParams(window.location.search).get("view") === "pallets" ? "pallets" : null));
    const [palletsVersion, setPalletsVersion] = useState(0);   // bump to reload the Pallets view after an upload
    const [tableTab, setTableTab]           = useState(null);   // table report while in the "all" view
    const [showIncoterms, setShowIncoterms] = useState(false);
    const [showUploads, setShowUploads]     = useState(false);
    const [historyId, setHistoryId]         = useState(null);
    const [mapFocus, setMapFocus]           = useState(null);   // outbound shipment selected for the map
    const [tableOpen, setTableOpen]         = useState(false);  // outbound: shipments table in an overlay
    const [termsOpen, setTermsOpen]         = useState(false);  // shipping terms (Incoterm) overview in an overlay
    const tableRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const [summary, current] = await Promise.allSettled([
            tenantOnly ? Promise.resolve({ data: null }) : api.get("/api/control-tower/summary"),
            api.get("/api/excel-ingestion/current"),
        ]);
        if (summary.status === "fulfilled") {
            setData(summary.value.data);
            setLast(new Date());
        } else {
            setError(summary.reason?.response?.data?.error || summary.reason?.message);
        }
        if (tenantOnly && current.status === "rejected") {
            setError(current.reason?.response?.data?.error || current.reason?.message);
        }
        // Excel data is optional — the Control Tower still works without it
        setExcel(current.status === "fulfilled" ? current.value.data : null);
        setLoading(false);
    }, [tenantOnly]);

    useEffect(() => { load(); }, [load]);

    const excelReports = excel?.reports || {};
    const statusLabels = excel?.statusLabels || {};
    const { firstReport, activeView, activeTab, xl, viewMissing } = resolveView({ view, tableTab, excelReports, fallback: tenantOnly ? "inbound" : "platform" });
    const rows = useExcelRows({ reportType: activeTab, report: excelReports[activeTab], statusLabels });
    const dash = buildDashboard({ data, xl, viewMissing });
    // Outbound reports recognised by the rules besides the carrier report (Dispatch / Shipped below)
    const otherOutbound = ["outstanding", "prealert", "mfexport", "tnt"].map(k => excel?.[k]).filter(Boolean);

    // Filters are per report — reset when switching
    const selectView = v => { setView(v); setTableTab(null); rows.resetFilters(); setMapFocus(null); };
    // Table tab click: inside "all" it only changes the table; otherwise it switches the whole view
    const switchTab = id => {
        if (activeView === "all" && id !== "platform") { setTableTab(id); rows.resetFilters(); }
        else selectView(id);
    };
    // KPI card click → filter the shipment table to the same bucket (click again to clear)
    const openFiltered = filter => {
        if (activeTab === "platform") selectView(firstReport || "inbound");
        rows.setRowFilter(prev => (prev === filter && activeTab !== "platform" ? "" : filter));
        if (activeView === "outbound") return setTableOpen(true);   // outbound keeps the table in an overlay
        tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const openUploads = () => setShowUploads(true);
    // After an import: reload the dashboard data and the Pallets view
    const onImported = () => { load(); setPalletsVersion(v => v + 1); };
    const openIncoterms = () => setShowIncoterms(true);

    return (
        <div className="min-h-screen bg-[#0B1120] text-white font-sans p-5 space-y-4">

            <DashboardHeader
                lastRefresh={lastRefresh} hasExcel={!!xl} excelReports={excelReports} storage={excel?.storage} otherOutbound={otherOutbound}
                activeView={activeView} loading={loading} tenantOnly={tenantOnly}
                onSelectView={selectView} onUpload={openUploads} onIncoterms={openIncoterms} onRefresh={load}
            />

            {showIncoterms && <IncotermsModal onClose={() => setShowIncoterms(false)}/>}
            {showUploads && <ExcelUploadManager onClose={() => setShowUploads(false)} onImported={onImported} storage={excel?.storage}/>}
            {historyId && <ExcelShipmentHistory shipmentId={historyId} onClose={() => setHistoryId(null)} onCommentAdded={rows.reload}/>}

            {activeView === "pallets" ? (
                <PalletsView refreshKey={palletsVersion} onUpload={openUploads}/>
            ) : (<>
                {viewMissing && !loading && !(activeView === "outbound" && otherOutbound.length > 0) && (
                    <div className="bg-[#111827] border border-[#1F2937] rounded-xl px-4 py-3 text-sm text-slate-400 flex items-center justify-between gap-3 flex-wrap">
                        <span>{activeView === "all" ? "No reports have been uploaded yet, so the All view is empty." : `No ${activeView} report has been uploaded yet, so the ${activeView} view is empty.`}</span>
                        <button onClick={openUploads} className="text-green-400 hover:underline flex items-center gap-1.5">
                            <FileSpreadsheet size={13}/> Upload {activeView === "all" ? "a" : `the ${activeView}`} report
                        </button>
                    </div>
                )}

                {error && (
                    <div className="bg-red-900/30 border border-red-800 rounded-xl px-4 py-3 text-sm text-red-400">
                        Failed to load: {error} — {tenantOnly ? "please refresh, or contact your TinyGlobal administrator if this continues." : "make sure the backend is running and you're logged in as admin."}
                    </div>
                )}

                {/* Outbound has its own metric row (OutboundInsights); these cards read the carrier
                    report only, so they show there just when that report is loaded */}
                {(activeView !== "outbound" || excelReports.outbound) && (
                    <TopKpiCards
                        kpis={dash.kpis} shipKpis={dash.shipKpis} xl={xl} metNote={dash.metNote} loading={loading}
                        rowFilter={rows.rowFilter} activeTab={activeTab} activeView={activeView} onCardClick={openFiltered}
                        shipmentsOnly={tenantOnly}
                    />
                )}

                {/* Inbound: still-incoming POs by class (rules: PO_CLASSES — 444 STO, 453 FOB) */}
                {activeView === "inbound" && excelReports.inbound?.aggregates?.poClasses && (() => {
                    const pc = excelReports.inbound.aggregates.poClasses;
                    const CLASSES = [["FOB", "FOB POs (453…)", "text-sky-300"], ["STO", "Stock transfers (444…)", "text-violet-300"], ["OTHER-MDPO", "MDPO", "text-amber-300"], ["UNCLASSIFIED", "Unclassified", "text-slate-400"]];
                    return (
                        <div className="bg-[#111827] border border-[#1F2937] rounded-xl px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
                            <span className="font-bold text-white">{pc.incomingPos.toLocaleString()} incoming POs <span className="font-normal text-slate-500">(Booked + In Transit, unique)</span></span>
                            {CLASSES.filter(([k]) => k !== "UNCLASSIFIED" || pc.byClass[k]).map(([k, label, tone]) => (
                                <span key={k} className={tone}><span className="font-bold tabular-nums">{(pc.byClass[k] || 0).toLocaleString()}</span> {label}</span>
                            ))}
                            <span className="ml-auto text-[11px] text-slate-500">{pc.uniquePos.toLocaleString()} unique POs · {pc.rows.toLocaleString()} PO lines</span>
                        </div>
                    );
                })()}

                {/* Outbound: warehouse Outstanding Orders linked to the carrier report */}
                {activeView === "outbound" && (
                    <OutboundInsights refreshKey={palletsVersion} onUpload={openUploads}
                        onSelect={setMapFocus} selectedRef={mapFocus?.orderReference}
                        focus={mapFocus} onClearFocus={() => setMapFocus(null)} contacts={tenantOnly}/>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <ShipmentMap
                        rawLocs={dash.rawLocs} resolvedPins={dash.resolvedPins} routes={dash.routes} unmapped={dash.unmapped}
                        hasExcel={!!xl} activeView={activeView} viewMissing={viewMissing}
                        dc={activeView === "outbound" ? OUTBOUND_DC : null}
                        focus={activeView === "outbound" ? mapFocus : null} onClearFocus={() => setMapFocus(null)}
                    />
                    <div className="lg:col-span-4 flex flex-col gap-4">
                        <StatusDonut donutData={dash.donutData} donutTotal={dash.donutTotal} loading={loading}/>
                        <FreightTrendCard/>
                    </div>
                    <div className="lg:col-span-3 flex flex-col gap-4">
                        <ExceptionCenter exceptions={dash.exceptions} loading={loading}/>
                        <TasksAlerts kpis={dash.kpis} shipKpis={dash.shipKpis} loading={loading}/>
                    </div>
                </div>

                {/* Shipping terms: just a button — the Incoterm breakdown opens in an overlay */}
                <div className="flex justify-end">
                    <button onClick={() => setTermsOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#334155] text-xs text-slate-300 hover:border-blue-500/60 hover:text-white hover:bg-blue-500/10">
                        <Table2 size={13}/> Shipping terms overview
                    </button>
                </div>
                {termsOpen && (
                    <Overlay fit title="Shipping Terms Overview" sub="Pending shipments & POs grouped by Incoterm" onClose={() => setTermsOpen(false)}>
                        <div className="p-5">
                            <ShippingTermsOverview breakdown={data?.incoterms || []} loading={loading} onOpenGuide={() => { setTermsOpen(false); openIncoterms(); }}/>
                        </div>
                    </Overlay>
                )}

                {activeView === "outbound" ? (<>
                    {/* Outbound keeps the page to one screen: the carrier-report table opens in an overlay */}
                    <div className="bg-[#111827] border border-[#1F2937] rounded-xl px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <p className="text-sm font-bold text-white">Live Shipments &amp; ASNs</p>
                            <p className="text-[11px] text-slate-500">Every carrier-report row with filters, copy to Excel and row history</p>
                        </div>
                        <button onClick={() => setTableOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#334155] text-xs text-slate-300 hover:border-blue-500/60 hover:text-white hover:bg-blue-500/10">
                            <Table2 size={13}/> Open table
                        </button>
                    </div>
                    {tableOpen && (
                        <Overlay title="Live Shipments & ASNs" sub="Carrier report rows · click a row for its history" onClose={() => setTableOpen(false)}>
                            <ShipmentsTable
                                ref={tableRef}
                                activeTab={activeTab} activeView={activeView} excelReports={excelReports} rows={rows} tenantOnly={tenantOnly}
                                statusLabels={statusLabels} liveShipments={data?.liveShipments || []} loading={loading}
                                onSwitchTab={switchTab} onUpload={openUploads} onOpenRow={setHistoryId}
                            />
                        </Overlay>
                    )}
                </>) : (
                    <ShipmentsTable
                        ref={tableRef}
                        activeTab={activeTab} activeView={activeView} excelReports={excelReports} rows={rows} tenantOnly={tenantOnly}
                        statusLabels={statusLabels} liveShipments={data?.liveShipments || []} loading={loading}
                        onSwitchTab={switchTab} onUpload={openUploads} onOpenRow={setHistoryId}
                    />
                )}

                <BottomKpiCards cards={bottomKpis({ activeView, xl, kpis: dash.kpis })} loading={loading}/>
            </>)}
        </div>
    );
}
